import { useState } from 'react';
import Icon from './Icons';
import { requestCareProfile } from './api';

const DIFFICULTY_STYLE = {
  beginner:     { background: 'var(--green-light)', color: 'var(--green)', border: '1px solid var(--green-border)' },
  intermediate: { background: 'var(--event-repot-bg)', color: 'var(--event-repot-fg)', border: '1px solid var(--border)' },
  advanced:     { background: 'var(--status-deceased-bg)', color: 'var(--status-deceased-fg)', border: '1px solid var(--border)' },
};

const LIGHT_STYLE = {
  low:               { background: 'var(--surface-subtle)', color: 'var(--text-muted)', border: '1px solid var(--border)' },
  'low-medium':      { background: 'var(--event-repot-bg)', color: 'var(--event-repot-fg)', border: '1px solid var(--border)' },
  medium:            { background: 'var(--event-repot-bg)', color: 'var(--event-repot-fg)', border: '1px solid var(--border)' },
  'medium-bright':   { background: 'var(--event-repot-bg)', color: 'var(--event-repot-fg)', border: '1px solid var(--border)' },
  'bright indirect': { background: 'var(--event-repot-bg)', color: 'var(--event-repot-fg)', border: '1px solid var(--border)' },
  direct:            { background: 'var(--status-available-bg)', color: 'var(--status-available-fg)', border: '1px solid var(--border)' },
};

const DRY_PREF_LABEL = {
  dry_between:    'Dry between waterings',
  slightly_moist: 'Slightly moist',
  evenly_moist:   'Evenly moist',
  wet_tolerant:   'Wet tolerant',
};

const PROP_METHOD_LABEL = {
  stem_cutting:     'Stem Cutting',
  wet_stick:        'Wet Stick',
  division:         'Division',
  rhizome_division: 'Rhizome Division',
  air_layering:     'Air Layering',
  seed:             'Seed',
  offset:           'Offset',
  corm:             'Corm',
  leaf_cutting:     'Leaf Cutting',
  bulbil:           'Bulbil',
};

const SUCCESS_STYLE = {
  high:     { color: 'var(--green)' },
  moderate: { color: 'var(--event-repot-fg)' },
  low:      { color: 'var(--danger)' },
};

function Chip({ label, style }) {
  return (
    <span style={{
      display: 'inline-block',
      fontSize: '0.72rem',
      fontWeight: 600,
      borderRadius: 4,
      padding: '2px 7px',
      textTransform: 'capitalize',
      border: '1px solid var(--border)',
      background: 'var(--surface-recessed)',
      color: 'var(--text)',
      ...style,
    }}>
      {label}
    </span>
  );
}

function SummaryPill({ label, value, chipStyle }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', alignItems: 'flex-start' }}>
      <span style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
        {label}
      </span>
      <Chip label={value} style={chipStyle} />
    </div>
  );
}

const AFFECTED_LABEL = {
  pets:     'pets',
  children: 'children',
  both:     'pets & children',
};

function ToxicityBox({ toxicity }) {
  const [open, setOpen] = useState(false);
  const affectedLabel = AFFECTED_LABEL[toxicity.affected] || toxicity.affected;

  if (!toxicity.toxic) {
    return (
      <div style={{ padding: '0.4rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', background: 'var(--green-light, #f0f7f1)', border: '1px solid var(--green-border, #c3dfc9)', fontSize: '0.82rem', color: 'var(--green, #3a7d44)' }}>
        Non-toxic
      </div>
    );
  }

  return (
    <div style={{ borderRadius: 8, marginBottom: '0.75rem', background: 'var(--status-deceased-bg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          width: '100%', padding: '0.55rem 0.85rem',
          background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Icon name="alertTriangle" size={14} style={{ color: 'var(--danger)', flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--danger)', flex: 1 }}>
          Toxic
          {toxicity.severity && <span style={{ fontWeight: 400 }}> ({toxicity.severity})</span>}
          {affectedLabel && <span style={{ fontWeight: 400 }}> — {affectedLabel}</span>}
        </span>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} style={{ color: 'var(--danger)', flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ padding: '0 0.85rem 0.65rem' }}>
          {toxicity.description && <BodyText>{toxicity.description}</BodyText>}
        </div>
      )}
    </div>
  );
}

function CareSection({ title, children }) {
  return (
    <div style={{ padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function BodyText({ children }) {
  return <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text)', lineHeight: 1.55 }}>{children}</p>;
}

function DetailLine({ label, children }) {
  return (
    <p style={{ margin: '0 0 0.28rem', fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.5 }}>
      <strong>{label}:</strong> {children}
    </p>
  );
}

function requestButtonLabel(plant, requestedIdentity) {
  const profileType = requestedIdentity?.profile_type || (
    !plant?.species?.trim()
      ? (plant?.trade_name?.trim() ? 'trade_name' : 'genus')
      : (plant?.species?.includes('×') ? 'hybrid' : 'species')
  );
  const species = requestedIdentity?.species || plant?.species || '';
  const isCultivar = species.includes("'");
  if (profileType === 'genus') return 'Request genus-level care';
  if (profileType === 'trade_name') return 'Request trade-name care';
  if (isCultivar) return 'Request cultivar-specific care';
  if (profileType === 'hybrid') return 'Request hybrid-specific care';
  return 'Request species-specific care';
}

function RequestSpeciesButton({ requested, requesting, onClick, label }) {
  if (requested) {
    return <span style={{ fontSize: '0.82rem', color: 'var(--green, #3a7d44)', fontWeight: 600 }}>Request submitted</span>;
  }
  return (
    <button type="button" className="btn btn-secondary btn-sm" onClick={onClick} disabled={requesting}>
      {requesting ? 'Requesting…' : label}
    </button>
  );
}

export default function SpeciesCareProfile({
  plantId, plant, profile, loading, onRequested,
  hideIdentitySections = false,
  onOpenGuide,      // when set: "Care guide" button calls this; inline expansion is suppressed
  showFullGuide,    // when true: renders full guide content directly (for use in sheets)
}) {
  const [expanded, setExpanded] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [requested, setRequested] = useState(false);

  const handleRequest = async () => {
    setRequesting(true);
    setRequestError(null);
    try {
      await requestCareProfile(plantId);
      setRequested(true);
      if (onRequested) onRequested();
    } catch (e) {
      setRequestError(e.message);
    } finally {
      setRequesting(false);
    }
  };

  if (loading) return null;

  if (!profile) {
    const noSpecies = !plant?.species?.trim();
    const hasTradeName = !!plant?.trade_name?.trim();
    const isGenusOnly = noSpecies && !hasTradeName;
    const message = hasTradeName
      ? 'No care info available for this trade name yet.'
      : isGenusOnly
        ? `No genus-level care info is available for ${plant?.genus || 'this genus'} yet.`
        : 'No care info available for this species yet.';
    const detailMessage = isGenusOnly
      ? 'This plant does not have a species or trade name, so requesting care will create a general genus-level profile.'
      : null;
    return (
      <div className="detail-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <h2 className="section-title" style={{ margin: 0, border: 'none', paddingBottom: 0 }}>Care Info</h2>
        </div>
        <div style={{ padding: '0.75rem 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flex: '1 1 18rem', minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.84rem', color: 'var(--text-muted)' }}>
              {message}
            </p>
            {detailMessage && (
              <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                {detailMessage}
              </p>
            )}
          </div>
          {requested ? (
            <span style={{ fontSize: '0.82rem', color: 'var(--green, #3a7d44)' }}>Request submitted</span>
          ) : (
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleRequest} disabled={requesting}>
              {requesting ? 'Requesting…' : requestButtonLabel(plant)}
            </button>
          )}
        </div>
        {requestError && <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: 'var(--danger)' }}>{requestError}</p>}
      </div>
    );
  }

  if (!profile.profile_json) {
    return (
      <div className="detail-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <h2 className="section-title" style={{ margin: 0, border: 'none', paddingBottom: 0 }}>Care Info</h2>
        </div>
        <div style={{
          padding: '0.75rem 0.85rem',
          borderRadius: 8,
          border: '1px solid var(--border)',
          background: 'var(--surface-recessed)',
        }}>
          <div style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
            Care guidance unavailable
          </div>
          <p style={{ margin: 0, fontSize: '0.84rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {profile.message || 'No reviewed care guidance is available for this plant yet.'}
          </p>
          {profile.exact_status === 'rejected' && profile.public_message && (
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
              {profile.public_message}
            </p>
          )}
          {profile.can_request_species && (
            <div style={{ marginTop: '0.65rem' }}>
              <RequestSpeciesButton
                requested={requested}
                requesting={requesting}
                onClick={handleRequest}
                label={requestButtonLabel(plant, profile.requested_identity)}
              />
            </div>
          )}
          {!profile.can_request_species && profile.generation_job_status && profile.generation_job_status !== 'failed' && (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
              A profile is queued for generation.
            </p>
          )}
        </div>
        {requestError && <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: 'var(--danger)' }}>{requestError}</p>}
      </div>
    );
  }

  const p = profile.profile_json;
  const isGenusFallback = profile.match_type === 'genus_fallback';
  const isParentSpeciesFallback = profile.match_type === 'parent_species_fallback';
  const isFallback = isGenusFallback || isParentSpeciesFallback;
  const requestedIdentity = profile.requested_identity || {};
  const genusName = profile.fallback_identity?.genus || profile.genus || requestedIdentity.genus;
  const fallbackSpeciesId = profile.fallback_identity?.species;
  const speciesLabel = [requestedIdentity.genus, requestedIdentity.species].filter(Boolean).join(' ');
  const isGenusProfile = profile.profile_type === 'genus';
  const hasGrowthFormDetails = Boolean(
    p.growth_habit && (
      (Array.isArray(p.growth_habit.type) && p.growth_habit.type.length) ||
      p.growth_habit.mature_size_indoor ||
      p.growth_habit.support ||
      p.growth_habit.leaf_shape ||
      p.growth_habit.leaf_texture ||
      p.growth_habit.venation ||
      p.growth_habit.petiole ||
      p.growth_habit.stem_growth_form ||
      (Array.isArray(p.growth_habit.diagnostic_features) && p.growth_habit.diagnostic_features.length) ||
      p.dormancy
    )
  );

  // In sheet mode (showFullGuide) render without the detail-section card wrapper
  // or Care Info heading — the sheet header is the identity surface.
  const inner = (
    <>
      {/* Header row — only shown in inline (card) mode */}
      {!showFullGuide && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
          <h2 className="section-title" style={{ margin: 0, border: 'none', paddingBottom: 0 }}>Care Info</h2>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onOpenGuide ? onOpenGuide : () => setExpanded(e => !e)}
            style={{ minWidth: '5.5rem' }}
          >
            {onOpenGuide ? 'Care guide' : (expanded ? 'Collapse' : 'Care guide')}
          </button>
        </div>
      )}

      {isFallback && (
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.7rem 0.85rem',
          borderRadius: 8,
          border: '1px solid var(--border)',
          background: isParentSpeciesFallback ? 'var(--status-available-bg)' : 'var(--event-repot-bg)',
        }}>
          <div style={{ fontSize: '0.84rem', fontWeight: 700, color: isParentSpeciesFallback ? 'var(--status-available-fg)' : 'var(--event-repot-fg)', marginBottom: '0.25rem' }}>
            {isParentSpeciesFallback
              ? `${genusName} ${fallbackSpeciesId} care`
              : `General ${genusName} care`}
          </div>
          <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.5 }}>
            {isParentSpeciesFallback
              ? `Showing ${genusName} ${fallbackSpeciesId} care — a specific profile for ${speciesLabel || 'this form'} is not available yet.`
              : `This is genus-level guidance, not a specific profile for ${speciesLabel || 'this plant'}. It may miss species, cultivar, trade-name, or hybrid-specific needs.`}
          </p>
          {profile.fallback_reason === 'rejected' && (
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
              {profile.public_message || 'A more specific profile was reviewed but not approved, so requests are closed for now.'}
            </p>
          )}
          {profile.can_request_species && (
            <div style={{ marginTop: '0.65rem' }}>
              <RequestSpeciesButton
                requested={requested}
                requesting={requesting}
                onClick={handleRequest}
                label={requestButtonLabel(plant, requestedIdentity)}
              />
            </div>
          )}
          {!profile.can_request_species && profile.generation_job_status && profile.generation_job_status !== 'failed' && profile.fallback_reason !== 'rejected' && (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
              A more specific profile is queued for generation.
            </p>
          )}
          {requestError && <p style={{ margin: '0.35rem 0 0', fontSize: '0.82rem', color: 'var(--danger)' }}>{requestError}</p>}
        </div>
      )}

      {/* Summary pills — labeled so they're readable at a glance */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {p.care_difficulty && (
          <SummaryPill
            label="Difficulty"
            value={p.care_difficulty}
            chipStyle={DIFFICULTY_STYLE[p.care_difficulty]}
          />
        )}
        {p.light?.level && (
          <SummaryPill
            label="Light"
            value={p.light.level}
            chipStyle={LIGHT_STYLE[p.light.level]}
          />
        )}
        {p.water?.dry_preference && (
          <SummaryPill
            label="Water"
            value={DRY_PREF_LABEL[p.water.dry_preference] || p.water.dry_preference}
          />
        )}
        {p.growth_rate && (
          <SummaryPill label="Growth" value={p.growth_rate} />
        )}
      </div>

      {/* Expanded detail — shown when toggled inline OR when showFullGuide is set (sheet mode) */}
      {(expanded || showFullGuide) && (
        <div style={{ marginTop: showFullGuide ? 0 : '0.75rem' }}>

          {/* Distinguishing notes */}
          {!hideIdentitySections && p.distinguishing_notes && (
            <CareSection title="About">
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text)', lineHeight: 1.55, fontStyle: 'italic' }}>
                {p.distinguishing_notes}
              </p>
            </CareSection>
          )}

          {/* Growth & form */}
          {!hideIdentitySections && hasGrowthFormDetails && (
            <CareSection title="Growth & Form">
              {isGenusProfile && (
                <p style={{ margin: '0 0 0.35rem', fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                  These are broad traits shared by commonly grown members of {genusName || 'this genus'} and may not fit every species exactly.
                </p>
              )}
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.4rem' }}>
                {p.growth_rate && <Chip label={`${p.growth_rate} grower`} />}
                {Array.isArray(p.growth_habit?.type) && p.growth_habit.type.map(t => (
                  <Chip key={t} label={t.replace(/_/g, ' ')} />
                ))}
              </div>
              {p.growth_habit?.mature_size_indoor && (
                <p style={{ margin: '0 0 0.3rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Mature size: {p.growth_habit.mature_size_indoor}
                </p>
              )}
              {p.growth_habit?.support && (
                <p style={{ margin: '0 0 0.35rem', fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Support: {p.growth_habit.support.benefits_from?.replace(/_/g, ' ')}
                  {p.growth_habit.support.notes && ` — ${p.growth_habit.support.notes}`}
                </p>
              )}
              {p.growth_habit?.leaf_shape && <DetailLine label="Leaf shape">{p.growth_habit.leaf_shape}</DetailLine>}
              {p.growth_habit?.leaf_texture && <DetailLine label="Leaf texture">{p.growth_habit.leaf_texture}</DetailLine>}
              {p.growth_habit?.venation && <DetailLine label="Venation">{p.growth_habit.venation}</DetailLine>}
              {p.growth_habit?.petiole && <DetailLine label="Petiole">{p.growth_habit.petiole}</DetailLine>}
              {p.growth_habit?.stem_growth_form && <DetailLine label="Stem growth form">{p.growth_habit.stem_growth_form}</DetailLine>}
              {p.growth_habit?.diagnostic_features?.length > 0 && (
                <div style={{ marginTop: '0.35rem' }}>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                    Diagnostic Features
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {p.growth_habit.diagnostic_features.map((feature, i) => (
                      <Chip key={i} label={feature} />
                    ))}
                  </div>
                </div>
              )}
              {p.dormancy && (
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.85rem', color: 'var(--text)', lineHeight: 1.55 }}>
                  <strong>Dormancy:</strong> {p.dormancy}
                </p>
              )}
            </CareSection>
          )}

          {/* Toxicity — collapsible, collapsed by default */}
          {!hideIdentitySections && p.toxicity && <ToxicityBox toxicity={p.toxicity} />}

          {/* Light */}
          {p.light && (
            <CareSection title="Light">
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.4rem' }}>
                {p.light.level && <Chip label={p.light.level} style={LIGHT_STYLE[p.light.level]} />}
                {p.light.lux && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{p.light.lux}</span>}
              </div>
              {p.light.description && <BodyText>{p.light.description}</BodyText>}
            </CareSection>
          )}

          {/* Water */}
          {p.water && (
            <CareSection title="Water">
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.4rem' }}>
                {p.water.dry_preference && (
                  <Chip label={DRY_PREF_LABEL[p.water.dry_preference] || p.water.dry_preference} />
                )}
                {p.water.frequency && (
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{p.water.frequency}</span>
                )}
              </div>
              {p.water.description && <BodyText>{p.water.description}</BodyText>}
            </CareSection>
          )}

          {/* Humidity & Temperature */}
          {(p.humidity || p.temperature) && (
            <CareSection title="Humidity & Temperature">
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '0.4rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                {p.humidity?.ideal_range && (
                  <span>Humidity: <strong style={{ color: 'var(--text)' }}>{p.humidity.ideal_range}</strong>
                    {p.humidity.min_percent != null && <span> (min {p.humidity.min_percent}%)</span>}
                  </span>
                )}
                {p.temperature?.min_f != null && (
                  <span>Temp: <strong style={{ color: 'var(--text)' }}>{p.temperature.min_f}–{p.temperature.max_f}°F / {p.temperature.min_c}–{p.temperature.max_c}°C</strong></span>
                )}
              </div>
              {p.humidity?.description && <BodyText>{p.humidity.description}</BodyText>}
              {p.temperature?.description && (
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.85rem', color: 'var(--text)', lineHeight: 1.55 }}>
                  {p.temperature.description}
                </p>
              )}
            </CareSection>
          )}

          {/* Soil */}
          {p.soil && (
            <CareSection title="Soil">
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                {p.soil.drainage_preference && (
                  <Chip label={p.soil.drainage_preference.replace(/_/g, ' ')} />
                )}
                {p.soil.epiphytic && <Chip label="Epiphytic" />}
              </div>
              {p.soil.description && <BodyText>{p.soil.description}</BodyText>}
            </CareSection>
          )}

          {/* Fertilization */}
          {p.fertilization && (
            <CareSection title="Fertilization">
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.4rem' }}>
                {p.fertilization.sensitivity && (
                  <Chip label={`${p.fertilization.sensitivity} sensitivity`} />
                )}
                {p.fertilization.frequency && (
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{p.fertilization.frequency}</span>
                )}
              </div>
              {p.fertilization.notes && <BodyText>{p.fertilization.notes}</BodyText>}
            </CareSection>
          )}

          {/* Propagation — method + success rate on one line, notes below */}
          {p.propagation_methods?.length > 0 && (
            <CareSection title="Propagation">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {p.propagation_methods.map((m, i) => (
                  <div key={i}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: m.notes ? '0.2rem' : 0 }}>
                      <span style={{ fontSize: '0.83rem', fontWeight: 600, color: 'var(--text)' }}>
                        {PROP_METHOD_LABEL[m.method] || m.method}
                      </span>
                      {m.success_rate && (
                        <span style={{ fontSize: '0.74rem', fontWeight: 600, ...SUCCESS_STYLE[m.success_rate] }}>
                          {m.success_rate} success
                        </span>
                      )}
                    </div>
                    {m.notes && (
                      <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{m.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </CareSection>
          )}

          {/* Common pests */}
          {p.common_pests?.length > 0 && (
            <CareSection title="Common Pests">
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {p.common_pests.map((pest, i) => (
                  <Chip key={i} label={pest} />
                ))}
              </div>
            </CareSection>
          )}

          {/* Common problems */}
          {p.common_problems?.length > 0 && (
            <CareSection title="Common Problems">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {p.common_problems.map((prob, i) => (
                  <div key={i}>
                    <div style={{ fontSize: '0.83rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.2rem' }}>
                      {prob.issue}
                    </div>
                    <div style={{ fontSize: '0.81rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>
                      <strong>Cause:</strong> {prob.cause}
                    </div>
                    <div style={{ fontSize: '0.81rem', color: 'var(--text)', lineHeight: 1.5 }}>
                      {prob.solution}
                    </div>
                  </div>
                ))}
              </div>
            </CareSection>
          )}

          {/* Outdoor care */}
          {p.outdoor_care?.outdoor_suitable && (
            <CareSection title="Outdoor Care">
              <div style={{ marginBottom: '0.35rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {p.outdoor_care.sun_tolerance && <Chip label={p.outdoor_care.sun_tolerance.replace(/_/g, ' ')} />}
                {p.outdoor_care.min_outdoor_temp_f != null && (
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Min: {p.outdoor_care.min_outdoor_temp_f}°F / {p.outdoor_care.min_outdoor_temp_c}°C
                  </span>
                )}
              </div>
              {p.outdoor_care.acclimation_notes && <BodyText>{p.outdoor_care.acclimation_notes}</BodyText>}
              {p.outdoor_care.outdoor_risks && (
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: 'var(--text)', lineHeight: 1.55 }}>
                  {p.outdoor_care.outdoor_risks}
                </p>
              )}
            </CareSection>
          )}

          {/* Cultivar notes */}
          {p.cultivar_notes && (
            <CareSection title="Cultivar Notes">
              <BodyText>{p.cultivar_notes}</BodyText>
            </CareSection>
          )}

          {/* Grower notes */}
          {p.grower_notes && (
            <CareSection title="Grower Notes">
              <BodyText>{p.grower_notes}</BodyText>
            </CareSection>
          )}

          {/* Confidence flags */}
          {p.profile_notes && (
            <CareSection title="Notes on Confidence">
              <BodyText>{p.profile_notes}</BodyText>
            </CareSection>
          )}

          {/* Disclaimer — only shown in expanded view */}
          <div style={{
            marginTop: '0.75rem',
            padding: '0.6rem 0.85rem',
            borderRadius: 8,
            background: 'var(--surface-recessed)',
            border: '1px solid var(--border)',
            fontSize: '0.74rem',
            color: 'var(--text-muted)',
            lineHeight: 1.55,
          }}>
            Care information is AI-generated and reviewed by a human admin before publishing. It is provided for general reference only — always verify care details against authoritative sources. Toxicity information should not be relied upon for medical or veterinary decisions; consult a poison control center or veterinarian in any emergency. No liability is assumed for the accuracy of information presented here.
          </div>
        </div>
      )}
    </>
  );

  // Sheet mode: no card wrapper, no extra box-shadow
  if (showFullGuide) return inner;

  return <div className="detail-section">{inner}</div>;
}
